import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Table, Badge, Tabs, Tab } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import '../assets/css/KelolaTabs.css';
import { collection, query, where, getDocs, orderBy, doc, deleteDoc, updateDoc, addDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaEye, FaEdit, FaTrash, FaPlus, FaCheck, FaTimes, FaFileAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SeminarDetail from '../components/seminar/SeminarDetail';
import SeminarForm from '../components/seminar/SeminarForm';
import RekapNilaiAkhir from '../components/seminar/RekapNilaiAkhir';

const KelolaSeminar = () => {
  const { currentUser } = useAuth();
  const [seminarList, setSeminarList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedSeminar, setSelectedSeminar] = useState(null);
  const [activeTab, setActiveTab] = useState('usulan');
  const [usulanList, setUsulanList] = useState([]);
  const [showRekapModal, setShowRekapModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Ambil semua seminar terjadwal
      const seminarQuery = query(
        collection(db, "seminar"),
        orderBy("tanggal", "desc")
      );
      const seminarSnapshot = await getDocs(seminarQuery);
      
      const seminarData = [];
      for (const doc of seminarSnapshot.docs) {
        seminarData.push({
          id: doc.id,
          ...doc.data()
        });
      }
      
      setSeminarList(seminarData);
      
      // Ambil semua usulan seminar dari dosen
      try {
        const usulanQuery = query(
          collection(db, "usulanSeminar"),
          orderBy("createdAt", "desc")
        );
        const usulanSnapshot = await getDocs(usulanQuery);
        
        const usulanData = [];
        for (const doc of usulanSnapshot.docs) {
          usulanData.push({
            id: doc.id,
            ...doc.data()
          });
        }
        
        setUsulanList(usulanData);
      } catch (usulanError) {
        console.error("Error fetching usulan data:", usulanError);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Terjadi kesalahan saat mengambil data");
    } finally {
      setLoading(false);
    }
  };

  const handleShowDetail = (seminar) => {
    setSelectedSeminar(seminar);
    setShowDetailModal(true);
  };

  const handleShowForm = (seminar = null) => {
    setSelectedSeminar(seminar);
    setShowFormModal(true);
  };

  const handleCloseForm = (refresh = false) => {
    setShowFormModal(false);
    if (refresh) {
      fetchData();
    }
  };
  
  // Function to delete a seminar - with improved error handling
  const handleDeleteSeminar = async (seminar) => {
    try {
      // Make sure we have a valid seminar object with id
      if (!seminar || !seminar.id || typeof seminar.id !== 'string') {
        setError("Data seminar tidak valid untuk dihapus");
        return;
      }
      
      // Get seminar type for display
      const seminarType = seminar.jenis === 'proposal' ? 'Seminar Proposal' : 
                         seminar.jenis === 'hasil' ? 'Seminar Hasil' : 'Sidang Komprehensif';
      
      if (!window.confirm(`Yakin ingin menghapus ${seminarType} ini?`)) {
        return;
      }
      
      setLoading(true);
      
      // Create a valid document reference
      const seminarDocRef = doc(db, "seminar", seminar.id);
      
      // Verify document exists before attempting to delete
      const docSnap = await getDoc(seminarDocRef);
      if (!docSnap.exists()) {
        console.warn("Attempting to delete non-existent document:", seminar.id);
        setError("Seminar sudah dihapus atau tidak ditemukan");
        return;
      }
      
      // Delete the seminar document
      await deleteDoc(seminarDocRef);
      
      setSuccess(`${seminarType} berhasil dihapus.`);
      
      // Refresh the data
      fetchData();
    } catch (error) {
      console.error("Error deleting seminar:", error);
      setError(`Terjadi kesalahan saat menghapus seminar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fungsi untuk setujui usulan seminar
  const handleApproveUsulan = async (usulan) => {
    try {
      setLoading(true);
      
      // 1. Validasi: Cek apakah sudah ada seminar scheduled dengan jenis yang sama
      const existingScheduledQuery = query(
        collection(db, "seminar"),
        where("tugasAkhirId", "==", usulan.tugasAkhirId),
        where("jenis", "==", usulan.jenis),
        where("status", "==", "scheduled")
      );
      
      const existingScheduledSnapshot = await getDocs(existingScheduledQuery);
      const scheduledSeminarsToCancel = [];
      
      if (!existingScheduledSnapshot.empty) {
        // Ada seminar scheduled yang perlu dibatalkan
        existingScheduledSnapshot.forEach((seminarDoc) => {
          scheduledSeminarsToCancel.push({
            id: seminarDoc.id,
            ...seminarDoc.data()
          });
        });
      }
      
      // 2. Jika ini reschedule dan ada previousSeminarId, mark seminar lama sebagai superseded
      const previousSeminarUpdates = [];
      if (usulan.isReschedule && usulan.previousSeminarId) {
        previousSeminarUpdates.push(
          updateDoc(doc(db, "seminar", usulan.previousSeminarId), {
            superseded: true,
            supersededAt: new Date().toISOString(),
            supersededReason: "Digantikan oleh seminar reschedule yang baru"
          })
        );
      }
      
      // 3. Cancel semua seminar scheduled dengan jenis yang sama
      const cancelUpdates = scheduledSeminarsToCancel.map((seminar) => 
        updateDoc(doc(db, "seminar", seminar.id), {
          status: 'cancelled',
          cancelledAt: new Date().toISOString(),
          cancelledReason: `Dibatalkan karena ada seminar ${usulan.isReschedule ? 'reschedule' : 'baru'} yang disetujui`,
          cancelledBy: currentUser.uid
        })
      );
      
      // 4. Create seminar baru
      const seminarData = {
        jenis: usulan.jenis,
        tugasAkhirId: usulan.tugasAkhirId,
        mahasiswaId: usulan.mahasiswaId,
        mahasiswaNama: usulan.mahasiswaNama,
        mahasiswaNIM: usulan.mahasiswaNIM,
        judulTA: usulan.judulTA,
        tanggal: new Date().toISOString().split('T')[0], // Default tanggal hari ini, akan diedit nanti
        waktu: "09:00", // Default jam, akan diedit nanti
        tempat: "Ruang Seminar", // Default tempat, akan diedit nanti
        diajukanOleh: usulan.diajukanOleh,
        status: 'scheduled',
        createdAt: new Date().toISOString(),
        // Tandai jika ini adalah seminar ulang (reschedule)
        isReschedule: usulan.isReschedule || false,
        // Jika ada data seminar sebelumnya yang gagal
        previousSeminarId: usulan.previousSeminarId || null,
        // Jika disetujui oleh Pembimbing 1 terlebih dahulu
        pembimbing1Approval: usulan.pembimbing1Approval || null,
        pembimbing1Id: usulan.pembimbing1Id || null,
        pembimbing1Nama: usulan.pembimbing1Nama || null
      };
      
      // 5. Simpan seminar baru ke Firestore
      const newSeminarRef = await addDoc(collection(db, "seminar"), seminarData);
      
      // 6. Update previousSeminarId untuk tambahkan rescheduledToSeminarId
      if (usulan.isReschedule && usulan.previousSeminarId) {
        previousSeminarUpdates.push(
          updateDoc(doc(db, "seminar", usulan.previousSeminarId), {
            rescheduledToSeminarId: newSeminarRef.id
          })
        );
      }
      
      // 7. Eksekusi semua update (mark superseded dan rescheduledToSeminarId)
      if (previousSeminarUpdates.length > 0) {
        await Promise.all(previousSeminarUpdates);
      }
      
      // 8. Eksekusi cancel updates
      if (cancelUpdates.length > 0) {
        await Promise.all(cancelUpdates);
      }
      
      // 9. Update status usulan menjadi approved
      const usulanRef = doc(db, "usulanSeminar", usulan.id);
      await updateDoc(usulanRef, { 
        status: 'approved',
        approvedBy: {
          admin: currentUser.uid,
          timestamp: new Date().toISOString()
        },
        newSeminarId: newSeminarRef.id
      });
      
      // Pesan sukses yang sesuai dengan jenis usulan
      let successMessage = usulan.isReschedule 
        ? "Usulan penjadwalan ulang seminar berhasil disetujui dan jadwal baru telah dibuat." 
        : "Usulan seminar berhasil disetujui dan jadwal telah dibuat.";
      
      if (scheduledSeminarsToCancel.length > 0) {
        successMessage += ` ${scheduledSeminarsToCancel.length} seminar scheduled lama telah dibatalkan.`;
      }
      
      setSuccess(successMessage);
      fetchData();
    } catch (error) {
      console.error("Error approving usulan:", error);
      setError("Terjadi kesalahan saat menyetujui usulan: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Fungsi untuk tolak usulan seminar
  const handleRejectUsulan = async (usulan) => {
    try {
      if (!window.confirm("Yakin ingin menolak usulan seminar ini?")) return;
      
      setLoading(true);
      
      // Update status usulan menjadi rejected
      const usulanRef = doc(db, "usulanSeminar", usulan.id);
      await updateDoc(usulanRef, { 
        status: 'rejected',
        rejectedBy: {
          admin: currentUser.uid,
          timestamp: new Date().toISOString()
        }
      });
      
      setSuccess("Usulan seminar telah ditolak.");
      fetchData();
    } catch (error) {
      console.error("Error rejecting usulan:", error);
      setError("Terjadi kesalahan saat menolak usulan");
    } finally {
      setLoading(false);
    }
  };
  
  // Render tabel usulan
  const renderUsulanTable = () => {
    if (usulanList.length === 0) {
      return (
        <Alert variant="info">
          Belum ada usulan seminar dari dosen pembimbing
        </Alert>
      );
    }
    
    // Filter usulan yang statusnya masih pending atau menunggu persetujuan admin setelah disetujui P1
    const pendingUsulan = usulanList.filter(u => 
      u.status === 'pending_approval' || 
      !u.status || 
      u.status === 'pending_admin_approval'
    );
    
    if (pendingUsulan.length === 0) {
      return (
        <Alert variant="success">
          Semua usulan seminar sudah diproses
        </Alert>
      );
    }
    
    return (
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>No</th>
            <th>Mahasiswa</th>
            <th>Judul</th>
            <th>Jenis</th>
            <th>Status</th>
            <th>Diajukan Oleh</th>
            <th>Tanggal Usulan</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {pendingUsulan.map((usulan, index) => (
            <tr key={usulan.id}>
              <td>{index + 1}</td>
              <td>
                {usulan.mahasiswaNama}<br />
                <small>{usulan.mahasiswaNIM}</small>
              </td>
              <td>{usulan.judulTA}</td>
              <td>
                <div className="d-flex flex-column">
                  <Badge bg="info">
                    {usulan.jenis === 'proposal' ? 'Seminar Proposal' : 
                     usulan.jenis === 'hasil' ? 'Seminar Hasil' : 
                     'Sidang Komprehensif'}
                  </Badge>
                  {usulan.isReschedule && (
                    <Badge bg="warning" className="mt-1">Ulang</Badge>
                  )}
                </div>
              </td>
              <td>
                <Badge bg={
                  usulan.status === 'pending_p1_approval' ? 'secondary' :
                  usulan.status === 'pending_admin_approval' ? 'primary' :
                  'info'
                }>
                  {usulan.status === 'pending_p1_approval' ? 'Menunggu Persetujuan P1' :
                   usulan.status === 'pending_admin_approval' ? 'Disetujui P1' :
                   'Menunggu Persetujuan Admin'}
                </Badge>
                {usulan.statusMessage && (
                  <div className="mt-1">
                    <small>{usulan.statusMessage}</small>
                  </div>
                )}
              </td>
              <td>
                {usulan.diajukanOleh?.nama || 'Dosen'}<br />
                <small>{new Date(usulan.diajukanOleh?.timestamp).toLocaleDateString()}</small>
              </td>
              <td>{new Date(usulan.createdAt).toLocaleDateString()}</td>
              <td>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    onClick={() => handleApproveUsulan(usulan)}
                    disabled={loading}
                    title="Setujui dan buat jadwal"
                  >
                    <FaCheck />
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleRejectUsulan(usulan)}
                    disabled={loading}
                    title="Tolak usulan"
                  >
                    <FaTimes />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const getFilteredSeminar = (type) => {
    return seminarList.filter(seminar => seminar.jenis === type);
  };

  const renderSeminarTable = (type) => {
    const filteredSeminar = getFilteredSeminar(type);
    
    if (filteredSeminar.length === 0) {
      return (
        <Alert variant="info">
          Belum ada jadwal {type === 'proposal' ? 'seminar proposal' : type === 'hasil' ? 'seminar hasil' : 'sidang komprehensif'}
        </Alert>
      );
    }

    return (
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>No</th>
            <th>Mahasiswa</th>
            <th>Judul TA</th>
            <th>Tanggal</th>
            <th>Tempat</th>
            <th>Status</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {filteredSeminar.map((seminar, index) => (
            <tr key={seminar.id}>
              <td>{index + 1}</td>
              <td>{seminar.mahasiswaNama || '-'}</td>
              <td>{seminar.judulTA || '-'}</td>
              <td>{new Date(seminar.tanggal).toLocaleDateString()}</td>
              <td>{seminar.tempat}</td>
              <td>
                <div className="d-flex flex-column">
                  <Badge bg={
                    seminar.status === 'scheduled' ? 'info' :
                    seminar.status === 'completed' ? 'success' :
                    seminar.status === 'cancelled' ? 'danger' : 'secondary'
                  }>
                    {seminar.status === 'scheduled' ? 'Terjadwal' :
                     seminar.status === 'completed' ? 'Selesai' :
                     seminar.status === 'cancelled' ? 'Dibatalkan' : seminar.status}
                  </Badge>
                  
                  {seminar.isReschedule && (
                    <Badge bg="warning" className="mt-1">Ulang</Badge>
                  )}
                </div>
              </td>
              <td>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-info" 
                    size="sm"
                    onClick={() => handleShowDetail(seminar)}
                    title="Lihat Detail"
                  >
                    <FaEye />
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleShowForm(seminar)}
                    title="Edit Seminar"
                  >
                    <FaEdit />
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleDeleteSeminar(seminar)}
                    title="Hapus Seminar"
                  >
                    <FaTrash />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  // Kelompokkan seminar yang sudah selesai per tugas akhir (mahasiswa)
  // untuk keperluan rekap nilai akhir gabungan.
  const getGroupedByStudent = () => {
    const groups = {};

    seminarList.forEach((seminar) => {
      if (seminar.status !== 'completed') return;
      if (seminar.superseded === true) return;
      if (!seminar.tugasAkhirId) return;
      if (!['proposal', 'hasil', 'kompre'].includes(seminar.jenis)) return;

      if (!groups[seminar.tugasAkhirId]) {
        groups[seminar.tugasAkhirId] = {
          tugasAkhirId: seminar.tugasAkhirId,
          mahasiswaNama: seminar.mahasiswaNama,
          mahasiswaNIM: seminar.mahasiswaNIM,
          judulTA: seminar.judulTA,
          proposal: null,
          hasil: null,
          kompre: null
        };
      }

      const existing = groups[seminar.tugasAkhirId][seminar.jenis];
      // Pilih yang terbaru jika ada beberapa seminar selesai dengan jenis sama
      if (!existing || new Date(seminar.tanggal) >= new Date(existing.tanggal)) {
        groups[seminar.tugasAkhirId][seminar.jenis] = seminar;
      }
    });

    return Object.values(groups);
  };

  const handleShowRekap = (group) => {
    setSelectedGroup(group);
    setShowRekapModal(true);
  };

  const renderNilaiAkhirTable = () => {
    const groups = getGroupedByStudent();

    if (groups.length === 0) {
      return (
        <Alert variant="info">
          Belum ada seminar/sidang yang selesai untuk direkap nilai akhirnya
        </Alert>
      );
    }

    return (
      <>
        <Alert variant="secondary" className="py-2">
          Nilai akhir dihitung dari Seminar Proposal (30%), Seminar Hasil (40%),
          dan Sidang Komprehensif (30%). Berita acara ditandatangani seluruh
          pembimbing & penguji dan diketahui Koordinator Tugas Akhir.
        </Alert>
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>No</th>
              <th>Mahasiswa</th>
              <th>Judul TA</th>
              <th className="text-center">Proposal</th>
              <th className="text-center">Hasil</th>
              <th className="text-center">Kompre</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group, index) => (
              <tr key={group.tugasAkhirId}>
                <td>{index + 1}</td>
                <td>
                  {group.mahasiswaNama || '-'}<br />
                  <small>{group.mahasiswaNIM || '-'}</small>
                </td>
                <td>{group.judulTA || '-'}</td>
                <td className="text-center">
                  <Badge bg={group.proposal ? 'success' : 'secondary'}>
                    {group.proposal ? 'Selesai' : 'Belum'}
                  </Badge>
                </td>
                <td className="text-center">
                  <Badge bg={group.hasil ? 'success' : 'secondary'}>
                    {group.hasil ? 'Selesai' : 'Belum'}
                  </Badge>
                </td>
                <td className="text-center">
                  <Badge bg={group.kompre ? 'success' : 'secondary'}>
                    {group.kompre ? 'Selesai' : 'Belum'}
                  </Badge>
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleShowRekap(group)}
                    title="Rekap & Berita Acara Nilai Akhir"
                  >
                    <FaFileAlt className="me-1" /> Rekap Nilai
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  if (loading && !seminarList.length) {
    return (
      <Container className="py-4">
        <Alert variant="info">Memuat data...</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Card.Title>Kelola Seminar & Sidang</Card.Title>
            <Button 
              variant="primary" 
              onClick={() => handleShowForm()}
            >
              <FaPlus className="me-2" />
              Tambah Jadwal Baru
            </Button>
          </div>
          
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
          >
            <Tab eventKey="usulan" title="Usulan Dosen">
              {renderUsulanTable()}
            </Tab>
            <Tab eventKey="proposal" title="Seminar Proposal">
              {renderSeminarTable('proposal')}
            </Tab>
            <Tab eventKey="hasil" title="Seminar Hasil">
              {renderSeminarTable('hasil')}
            </Tab>
            <Tab eventKey="kompre" title="Sidang Komprehensif">
              {renderSeminarTable('kompre')}
            </Tab>
            <Tab eventKey="nilaiAkhir" title="Nilai Akhir">
              {renderNilaiAkhirTable()}
            </Tab>
          </Tabs>
          
          {showDetailModal && selectedSeminar && (
            <SeminarDetail 
              seminar={selectedSeminar} 
              show={showDetailModal} 
              onHide={() => setShowDetailModal(false)} 
            />
          )}
          
          {showFormModal && (
            <SeminarForm
              seminar={selectedSeminar}
              show={showFormModal}
              onHide={handleCloseForm}
            />
          )}

          {showRekapModal && selectedGroup && (
            <RekapNilaiAkhir
              group={selectedGroup}
              show={showRekapModal}
              onHide={() => setShowRekapModal(false)}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default KelolaSeminar;
